import * as Device from 'expo-device';
import { SECURITY_EVENT_TYPES } from '../constants';
import { isWEB, isAndroid, isIOS } from '../../helpers/getPlatformType';
import logger from '../logger';

/**
 * Device Environment Detector
 * 
 * Monitors device characteristics, detects emulators, suspicious environments,
 * and analyzes device behavior patterns for security threats.
 */
export class DeviceEnvironmentDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.deviceBaseline = null;
        this.environmentChecks = [];
        this.suspiciousIndicators = [];
        this.lastEnvironmentCheck = Date.now();
    }

    /**
     * Initialize the device environment detector
     */
    async initialize() {
        try {
            logger.detector('Device Environment', 'initializing');

            // Create device baseline
            await this.createDeviceBaseline();

            // Perform initial environment analysis
            await this.performInitialEnvironmentAnalysis();

            // Set up continuous monitoring
            this.setupContinuousMonitoring();

            this.initialized = true;
            logger.detector('Device Environment', 'initialized');

        } catch (error) {
            logger.detector('Device Environment', 'failed', error);
            throw error;
        }
    }

    /**
     * Create device baseline profile
     */
    async createDeviceBaseline() {
        try {
            const deviceInfo = await this.getDeviceInfo();
            
            this.deviceBaseline = {
                ...deviceInfo,
                timestamp: Date.now(),
                userAgent: this.getUserAgent(),
                screenInfo: this.getScreenInfo(),
                timezoneInfo: this.getTimezoneInfo(),
                languageInfo: this.getLanguageInfo(),
                platformFeatures: this.getPlatformFeatures()
            };

            logger.verbose('Device baseline created:', this.deviceBaseline);
            
        } catch (error) {
            console.warn('RASP - Failed to create device baseline:', error);
        }
    }

    /**
     * Get device information using expo-device
     */
    async getDeviceInfo() {
        const deviceInfo = {
            isDevice: null,
            brand: null,
            manufacturer: null,
            modelName: null,
            modelId: null,
            designName: null,
            productName: null,
            deviceYearClass: null,
            totalMemory: null,
            supportedCpuArchitectures: null,
            osName: null,
            osVersion: null,
            osBuildId: null,
            osInternalBuildId: null,
            osBuildFingerprint: null,
            platformApiLevel: null,
            deviceName: null
        };

        try {
            // Basic device info
            deviceInfo.isDevice = Device.isDevice;
            deviceInfo.brand = Device.brand;
            deviceInfo.manufacturer = Device.manufacturer;
            deviceInfo.modelName = Device.modelName;
            deviceInfo.modelId = Device.modelId;
            deviceInfo.designName = Device.designName;
            deviceInfo.productName = Device.productName;
            deviceInfo.deviceYearClass = Device.deviceYearClass;
            deviceInfo.totalMemory = Device.totalMemory;
            deviceInfo.supportedCpuArchitectures = Device.supportedCpuArchitectures;
            deviceInfo.osName = Device.osName;
            deviceInfo.osVersion = Device.osVersion;
            deviceInfo.osBuildId = Device.osBuildId;
            deviceInfo.osInternalBuildId = Device.osInternalBuildId;
            deviceInfo.osBuildFingerprint = Device.osBuildFingerprint;
            deviceInfo.platformApiLevel = Device.platformApiLevel;
            deviceInfo.deviceName = Device.deviceName;

        } catch (error) {
            console.warn('RASP - Failed to get device info:', error);
        }

        return deviceInfo;
    }

    /**
     * Get user agent information
     */
    getUserAgent() {
        try {
            if (typeof navigator !== 'undefined' && navigator.userAgent) {
                return navigator.userAgent;
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get screen information
     */
    getScreenInfo() {
        try {
            const screenInfo = {};
            
            if (typeof screen !== 'undefined') {
                screenInfo.width = screen.width;
                screenInfo.height = screen.height;
                screenInfo.colorDepth = screen.colorDepth;
                screenInfo.pixelDepth = screen.pixelDepth;
            }
            
            if (typeof window !== 'undefined') {
                screenInfo.innerWidth = window.innerWidth;
                screenInfo.innerHeight = window.innerHeight;
                screenInfo.outerWidth = window.outerWidth;
                screenInfo.outerHeight = window.outerHeight;
                screenInfo.devicePixelRatio = window.devicePixelRatio;
            }
            
            return screenInfo;
        } catch (error) {
            return {};
        }
    }

    /**
     * Get timezone information
     */
    getTimezoneInfo() {
        try {
            return {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                timezoneOffset: new Date().getTimezoneOffset(),
                locale: Intl.DateTimeFormat().resolvedOptions().locale
            };
        } catch (error) {
            return {};
        }
    }

    /**
     * Get language information
     */
    getLanguageInfo() {
        try {
            const languageInfo = {};
            
            if (typeof navigator !== 'undefined') {
                languageInfo.language = navigator.language;
                languageInfo.languages = navigator.languages;
            }
            
            return languageInfo;
        } catch (error) {
            return {};
        }
    }

    /**
     * Get platform-specific features
     */
    getPlatformFeatures() {
        const features = {
            platform: null,
            webgl: false,
            canvas: false,
            localStorage: false,
            sessionStorage: false,
            indexedDB: false,
            webWorkers: false,
            serviceWorkers: false
        };

        try {
            if (isWEB()) {
                features.platform = 'web';
                features.webgl = this.checkWebGLSupport();
                features.canvas = typeof HTMLCanvasElement !== 'undefined';
                features.localStorage = typeof localStorage !== 'undefined';
                features.sessionStorage = typeof sessionStorage !== 'undefined';
                features.indexedDB = typeof indexedDB !== 'undefined';
                features.webWorkers = typeof Worker !== 'undefined';
                features.serviceWorkers = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
            } else if (isAndroid()) {
                features.platform = 'android';
            } else if (isIOS()) {
                features.platform = 'ios';
            }
            
        } catch (error) {
            console.warn('RASP - Failed to get platform features:', error);
        }

        return features;
    }

    /**
     * Check WebGL support
     */
    checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            return !!gl;
        } catch (error) {
            return false;
        }
    }

    /**
     * Perform initial environment analysis
     */
    async performInitialEnvironmentAnalysis() {
        try {
            // Check for emulator indicators
            await this.checkEmulatorIndicators();
            
            // Check for debugging tools
            await this.checkDebuggingTools();
            
            // Check for suspicious environment modifications
            await this.checkEnvironmentModifications();
            
            // Analyze device characteristics
            await this.analyzeDeviceCharacteristics();
            
        } catch (error) {
            console.warn('RASP - Initial environment analysis failed:', error);
        }
    }

    /**
     * Set up continuous monitoring
     */
    setupContinuousMonitoring() {
        // Set up periodic environment checks
        setInterval(() => {
            this.performEnvironmentCheck();
        }, 300000); // Check every 5 minutes
    }

    /**
     * Perform device environment check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            await this.performEnvironmentCheck();
        } catch (error) {
            console.warn('RASP - Device environment check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.DEVICE_ANOMALY, {
                message: 'Device environment check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Perform comprehensive environment check
     */
    async performEnvironmentCheck() {
        try {
            // Get current device info
            const currentDeviceInfo = await this.getDeviceInfo();
            
            // Compare with baseline
            await this.compareWithBaseline(currentDeviceInfo);
            
            // Check for emulator indicators
            await this.checkEmulatorIndicators();
            
            // Check for debugging tools
            await this.checkDebuggingTools();
            
            // Check for environment modifications
            await this.checkEnvironmentModifications();
            
            // Update environment checks history
            this.updateEnvironmentHistory(currentDeviceInfo);
            
            this.lastEnvironmentCheck = Date.now();
            
        } catch (error) {
            console.warn('RASP - Environment check failed:', error);
        }
    }

    /**
     * Compare current device info with baseline
     */
    async compareWithBaseline(currentDeviceInfo) {
        if (!this.deviceBaseline) return;

        try {
            const changes = [];
            
            // Check for critical changes
            const criticalFields = ['isDevice', 'brand', 'manufacturer', 'modelName', 'osName'];
            
            criticalFields.forEach(field => {
                if (this.deviceBaseline[field] !== currentDeviceInfo[field]) {
                    changes.push({
                        field,
                        baseline: this.deviceBaseline[field],
                        current: currentDeviceInfo[field]
                    });
                }
            });
            
            if (changes.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.DEVICE_ANOMALY, {
                    message: 'Device characteristics changed from baseline',
                    details: { changes }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Baseline comparison failed:', error);
        }
    }

    /**
     * Check for emulator indicators (MINIMAL - only obvious cases)
     */
    async checkEmulatorIndicators() {
        try {
            const indicators = [];

            // Only check the most obvious emulator indicators to avoid false positives

            // Android: Only flag obvious SDK emulators
            if (isAndroid()) {
                if (Device.manufacturer && Device.manufacturer.toLowerCase() === 'google' &&
                    Device.modelName && Device.modelName.toLowerCase().includes('sdk_gphone')) {
                    indicators.push('android_emulator');
                }
            }

            // iOS: Only flag obvious simulators
            if (isIOS()) {
                if (Device.modelName && Device.modelName.toLowerCase() === 'simulator') {
                    indicators.push('ios_simulator');
                }
            }

            // Only report if we have clear emulator evidence
            if (indicators.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.EMULATOR_DETECTED, {
                    message: 'Clear emulator indicators detected',
                    details: { indicators }
                });
            }

        } catch (error) {
            // Silently skip emulator checks if they fail
            return;
        }
    }

    /**
     * Check for debugging tools (DISABLED - too aggressive)
     */
    async checkDebuggingTools() {
        // Debugging tools detection disabled to avoid false positives
        // Development tools are legitimate and should not be flagged
        return;
    }

    /**
     * Check for environment modifications (MINIMAL - only critical tampering)
     */
    async checkEnvironmentModifications() {
        try {
            const modifications = [];

            // Only check for the most critical modifications that indicate real attacks
            // Skip native code checks as they can vary across environments

            // Check for obvious malicious globals
            const globalObj = global || window || {};
            const suspiciousGlobals = Object.keys(globalObj).filter(key => {
                const keyLower = key.toLowerCase();
                return (keyLower.includes('malware') ||
                       keyLower.includes('hack') ||
                       keyLower.includes('exploit') ||
                       keyLower.includes('inject')) && !keyLower.includes('react_devtools') && !keyLower.includes('rootsiblings');
            });

            if (suspiciousGlobals.length > 0) {
                modifications.push('suspicious_globals');
            }

            // Only report if we have clear evidence of malicious modification
            if (modifications.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.SUSPICIOUS_ENVIRONMENT, {
                    message: 'Suspicious environment modifications detected',
                    details: { modifications, suspiciousGlobals }
                });
            }

        } catch (error) {
            // Silently skip environment checks if they fail
            return;
        }
    }

    /**
     * Analyze device characteristics for anomalies
     */
    async analyzeDeviceCharacteristics() {
        try {
            const anomalies = [];

            // Check for unusual memory values
            if (Device.totalMemory && Device.totalMemory < 1000000000) { // Less than 1GB
                anomalies.push('low_memory');
            }
            
            // Check for unusual CPU architectures
            if (Device.supportedCpuArchitectures && 
                Device.supportedCpuArchitectures.includes('unknown')) {
                anomalies.push('unknown_cpu_architecture');
            }
            
            // Check for missing device name
            if (!Device.deviceName || Device.deviceName === 'unknown') {
                anomalies.push('missing_device_name');
            }
            
            // Check for unusual OS versions
            if (Device.osVersion && Device.osVersion.includes('unknown')) {
                anomalies.push('unknown_os_version');
            }
            
            if (anomalies.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.DEVICE_ANOMALY, {
                    message: 'Device characteristic anomalies detected',
                    details: { anomalies }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Device characteristics analysis failed:', error);
        }
    }

    /**
     * Update environment checks history
     */
    updateEnvironmentHistory(deviceInfo) {
        this.environmentChecks.push({
            ...deviceInfo,
            timestamp: Date.now()
        });
        
        // Keep only recent checks (last 50)
        if (this.environmentChecks.length > 50) {
            this.environmentChecks = this.environmentChecks.slice(-50);
        }
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Get device environment statistics
     */
    getStatistics() {
        return {
            baseline: this.deviceBaseline,
            checksPerformed: this.environmentChecks.length,
            suspiciousIndicators: this.suspiciousIndicators.length,
            lastCheck: this.lastEnvironmentCheck,
            isEmulator: Device.isDevice === false
        };
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            this.environmentChecks = [];
            this.suspiciousIndicators = [];
            this.initialized = false;

            logger.detector('Device Environment', 'cleanup');
        } catch (error) {
            logger.error('Device Environment Detector cleanup failed:', error);
        }
    }
}
